import theme from 'src/theme';

export const aiAgentSx = {
    wrapper: {
        mt: theme.spacing(12 / 8),
    },
    root: {
        display: 'flex',
        // `dvh` rather than `vh`: a phone browser counts its address bar in `vh`, which put the chat's
        // own input below the bottom of the screen. The 600px floor goes with the sidebar below a
        // desktop - it is taller than the phone it was supposed to protect.
        height: {xs: 'calc(100dvh - 150px)', md: 'calc(100vh - 110px)'},
        minHeight: {xs: 0, md: 600},
        bgcolor: theme.palette.common.white,
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 10px 30px rgba(0,0,0,0.07)',
    },
    chatArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        background: `
            radial-gradient(ellipse at 15% 0%, rgba(0, 127, 220, 0.04) 0%, transparent 60%),
            radial-gradient(ellipse at 85% 100%, rgba(40, 65, 157, 0.03) 0%, transparent 60%),
            #F9FAFC
        `,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: {xs: 1.5, md: 3},
        py: {xs: 1.5, md: 2},
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    },
    headerAvatarWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerAvatarPulse: {
        position: 'absolute',
        inset: -4,
        borderRadius: '14px',
        background: 'linear-gradient(135deg, rgba(0, 127, 220, 0.18) 0%, rgba(40, 65, 157, 0.12) 100%)',
        animation: 'agentHeaderPulse 2.8s ease-in-out infinite',
        '@keyframes agentHeaderPulse': {
            '0%, 100%': {transform: 'scale(1)', opacity: 1},
            '50%': {transform: 'scale(1.15)', opacity: 0.4},
        },
    },
    headerAvatar: {
        position: 'relative',
        width: 40,
        height: 40,
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #007FDC 0%, #28419D 50%, #007FDC 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
        fontSize: 22,
        boxShadow: '0 2px 8px rgba(0, 127, 220, 0.3)',
    },
    /** Opens the session list below a desktop, where it is a drawer rather than a column. */
    sessionsButton: {
        width: 44,
        height: 44,
        flexShrink: 0,
    },
    headerInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        // Lets the assistant's name shorten instead of pushing the buttons off the edge of a phone.
        minWidth: 0,
    },
    headerOnline: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
    },
    onlineDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: theme.palette.success.main,
        boxShadow: '0 0 0 2px rgba(57, 150, 70, 0.2)',
    },
    offlineDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: theme.palette.error.main,
        boxShadow: '0 0 0 2px rgba(230, 41, 48, 0.2)',
    },
    headerActions: {
        ml: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
    },
    talkToHumanButton: {
        textTransform: 'none' as const,
        fontWeight: 500,
        fontSize: '0.8rem',
        color: theme.palette.neutral[600],
        borderColor: 'rgba(0, 0, 0, 0.12)',
        px: 1.5,
        py: 0.5,
        borderRadius: '10px',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            bgcolor: 'rgba(0, 127, 220, 0.05)',
        },
    },
    messages: {
        flex: 1,
        overflow: 'auto',
        // A conversation on a phone needs the width more than it needs the margin.
        px: {xs: 1.5, md: 3},
        py: {xs: 2, md: 3},
        display: 'flex',
        flexDirection: 'column',
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        gap: 2,
    },
    error: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        gap: 1,
    },
    disabledFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 2,
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        bgcolor: 'rgba(255, 169, 64, 0.08)',
    },
    disabledFooterText: {
        color: theme.palette.warning.dark,
        fontSize: '0.85rem',
        textAlign: 'center' as const,
        maxWidth: 640,
    },
    welcome: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        px: 4,
        pb: 6,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '30%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 127, 220, 0.07) 0%, transparent 65%)',
            pointerEvents: 'none',
        },
    },
    welcomeIconWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 0.5,
    },
    welcomeIconPulse: {
        position: 'absolute',
        inset: -10,
        borderRadius: '26px',
        background: 'linear-gradient(135deg, rgba(0, 127, 220, 0.12) 0%, rgba(40, 65, 157, 0.08) 100%)',
        animation: 'agentPulse 2.8s ease-in-out infinite',
        '@keyframes agentPulse': {
            '0%, 100%': {transform: 'scale(1)', opacity: 1},
            '50%': {transform: 'scale(1.12)', opacity: 0.5},
        },
    },
    welcomeIcon: {
        position: 'relative',
        width: 72,
        height: 72,
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #007FDC 0%, #28419D 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
        fontSize: 36,
        boxShadow: '0 8px 28px rgba(0, 127, 220, 0.4)',
    },
    welcomeGreeting: {
        background: 'linear-gradient(135deg, #007FDC 0%, #28419D 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        fontWeight: 800,
        letterSpacing: '-0.5px',
        lineHeight: 1.2,
    },
    welcomeInputWrapper: {
        width: '100%',
        maxWidth: 580,
        mt: 1,
    },
};